import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  StripeCardNumberElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardCvcElementChangeEvent,
} from '@stripe/stripe-js';
import styles from './PaymentForm.module.scss';
import visaImage from 'assets/images/card-brands/visa.svg';
import mastercardImage from 'assets/images/card-brands/master-card.svg';
import amexImage from 'assets/images/card-brands/american-express.svg';
import discoverImage from 'assets/images/card-brands/discover.svg';
import jcbImage from 'assets/images/card-brands/jcb.svg';
import unionpayImage from 'assets/images/card-brands/union-pay.svg';
import dinersImage from 'assets/images/card-brands/diners-club.svg';
import defaultImage from 'assets/images/card-brands/default.svg';
import CustomInput from 'components/shared/forms/CustomInput';
import CountrySelect from 'components/shared/CountrySelect/CountrySelect';
import {
  setPaymentFormAddress,
  setPaymentFormAddressErrors,
  setPaymentFormCardholderName,
  setPaymentFormCardholderNameError
} from 'features/billing/formStates/paymentFormInfo/paymentFormInfoActions';
import {
  paymentFormAddressErrorsSelector,
  paymentFormAddressSelector,
  paymentFormCardholderNameErrorSelector,
  paymentFormCardholderNameSelector
} from 'features/billing/formStates/paymentFormInfo/paymentFormInfoSlice';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'app/store';
import { customerInfoSelector } from 'features/billing/customer/customerSlice';
import { addCardIntent } from 'api/billing/customers';
import { updateCustomerDefaultPaymentMethod } from 'features/billing/customer/customerActions';
import iso3166 from "iso-3166-2";
import DropdownSelect, { ISelectOption } from 'components/shared/forms/DropdownSelect/DropdownSelect';

export interface PaymentFormHandle {
  triggerSubmit: () => Promise<{ success: boolean, paymentMethodId: string }>;
}

const PaymentForm = forwardRef<PaymentFormHandle>((_, ref) => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();

  const customer = useSelector(customerInfoSelector);
  const address = useSelector(paymentFormAddressSelector);
  const addressErrors = useSelector(paymentFormAddressErrorsSelector);
  const cardholderName = useSelector(paymentFormCardholderNameSelector);
  const cardholderNameError = useSelector(paymentFormCardholderNameErrorSelector);

  const [isCardNumberComplete, setIsCardNumberComplete] = useState(false);
  const [isCardExpiryComplete, setIsCardExpiryComplete] = useState(false);
  const [isCardCvcComplete, setIsCardCvcComplete] = useState(false);

  const [showAddressInputs, setShowAddressInputs] = useState(address.country || false);
  const [errorMessage, setErrorMessage] = useState('');

  const [cardBrand, setCardBrand] = useState('unknown');
  const [cardErrors, setCardErrors] = useState({
    cardNumber: '',
    cardExpiry: '',
    cardCvc: ''
  });

  const getStateOptions = (countryCode: string): ISelectOption[] => {
    if (!["US", "CA"].includes(countryCode.toLocaleUpperCase())) {
      return [];
    }
    const countryData = iso3166.data[countryCode];
    if (!countryData?.sub) return [];

    return Object.entries(countryData.sub).map(([code, name]) => ({
      value: code,
      label: name.name
    }));
  };

  const stateOptions = getStateOptions(address.country);

  const handleCardNumberChange = (event: StripeCardNumberElementChangeEvent) => {
    setCardBrand(event.brand);
    setIsCardNumberComplete(event.complete);
    setCardErrors((prev) => ({
      ...prev,
      cardNumber: event.error ? event.error.message || '' : '',
    }));
  };

  const handleCardExpiryChange = (event: StripeCardExpiryElementChangeEvent) => {
    setIsCardExpiryComplete(event.complete);
    setCardErrors((prev) => ({
      ...prev,
      cardExpiry: event.error ? event.error.message || '' : '',
    }));
  };

  const handleCardCvcChange = (event: StripeCardCvcElementChangeEvent) => {
    setIsCardCvcComplete(event.complete);
    setCardErrors((prev) => ({
      ...prev,
      cardCvc: event.error ? event.error.message || '' : '',
    }));
  };

  const handleCardholderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^[A-Za-z\s\-']*$/;
    if (regex.test(value)) {
      dispatch(setPaymentFormCardholderName(value));
      dispatch(setPaymentFormCardholderNameError(''));
    } else {
      dispatch(setPaymentFormCardholderNameError('A name can only contain letters, spaces, hyphens, and apostrophes'));
    }
  };

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        fontSize: '16px',
        color: '#32325d',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
    },
  };

  const getCardBrandIcon = (brand: string) => {
    switch (brand) {
      case 'visa':
        return visaImage;
      case 'mastercard':
        return mastercardImage;
      case 'amex':
        return amexImage;
      case 'discover':
        return discoverImage;
      case 'jcb':
        return jcbImage;
      case 'diners':
        return dinersImage;
      case 'unionpay':
        return unionpayImage;
      default:
        return defaultImage;
    }
  };

  useImperativeHandle(ref, () => ({
    triggerSubmit: async () => {
      if (!stripe || !elements) return { success: true, paymentMethodId: customer.defaultPaymentMethodId };

      setErrorMessage('');
      await dispatch(setPaymentFormCardholderNameError(''));
      await dispatch(
        setPaymentFormAddressErrors({
          country: '',
          state: '',
          line1: '',
          postalCode: '',
          city: '',
        })
      );

      const requiredAddressFields = ['country', 'line1', 'postalCode', 'city'] as const;
      let addressHasError = false;
      const newAddressErrors = { ...addressErrors };

      for (const field of requiredAddressFields) {
        if (address[field].trim() === '') {
          newAddressErrors[field] = 'This field is required';
          addressHasError = true;
        } else {
          newAddressErrors[field] = '';
        }
      }

      if (addressHasError) {
        await dispatch(setPaymentFormAddressErrors(newAddressErrors));
      }

      const newCardErrors = {
        cardNumber: !isCardNumberComplete ? 'Please enter the card number' : '',
        cardExpiry: !isCardExpiryComplete ? 'Please enter the card expiry date' : '',
        cardCvc: !isCardCvcComplete ? 'Please enter the card CVC' : ''
      };
      setCardErrors(newCardErrors);

      const cardholderNameHasError = cardholderName.trim() === '';
      if (cardholderNameHasError) {
        await dispatch(setPaymentFormCardholderNameError("Please enter the cardholder's name"));
      }

      const unsuccessfulResult = { success: false, paymentMethodId: customer.defaultPaymentMethodId };
      if (
        addressHasError ||
        newCardErrors.cardNumber ||
        newCardErrors.cardExpiry ||
        newCardErrors.cardCvc ||
        cardholderNameHasError
      ) {
        return unsuccessfulResult;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        return unsuccessfulResult;
      }

      const cardIntentClientSecret = (await addCardIntent(customer.id)).data as string;
      if (!cardIntentClientSecret) {
        return unsuccessfulResult;
      }

      const { setupIntent, error } = await stripe.confirmCardSetup(cardIntentClientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: cardholderName,
            address: {
              country: address.country,
              line1: address.line1,
              line2: address.line2,
              postal_code: address.postalCode,
              city: address.city,
            },
          },
        },
      });

      if (error) {
        setErrorMessage(error.message || 'Error when setting up the card');
        console.error('Error when setting up the card:', error.message);
        return unsuccessfulResult;
      } else if (setupIntent.status === 'succeeded') {
        setErrorMessage('');

        const newPaymentMethodId = setupIntent.payment_method as string;
        await dispatch(updateCustomerDefaultPaymentMethod({ customerId: customer.id, paymentMethodId: newPaymentMethodId }));
        return { success: true, paymentMethodId: newPaymentMethodId };
      } else {
        return unsuccessfulResult;
      }
    }
  }));

  return (
    <form
      className={styles.paymentForm}
      onSubmit={(e) => e.preventDefault()}
    >
      <div className={styles.positionRelative}>
        <label className={styles.label}>
          Card Number
          <div className={`${styles.flex} ${styles.alignItemsCenter}`}>
            <CardNumberElement
              options={CARD_ELEMENT_OPTIONS}
              className="form-control"
              onChange={handleCardNumberChange}
            />
            <img
              src={getCardBrandIcon(cardBrand)}
              alt="Card Brand"
              className={styles.cardBrandIcon}
            />
          </div>
          {cardErrors.cardNumber && (
            <p className={styles.fieldError}>
              {cardErrors.cardNumber}
            </p>
          )}
        </label>
      </div>

      <div className={styles.formGroupRow}>
        <label className={styles.label}>
          Expiration Date
          <CardExpiryElement
            options={CARD_ELEMENT_OPTIONS}
            className="form-control"
            onChange={handleCardExpiryChange}
          />
          {cardErrors.cardExpiry && (
            <p className={styles.fieldError}>
              {cardErrors.cardExpiry}
            </p>
          )}
        </label>
        <label className={styles.label}>
          CVC
          <CardCvcElement
            options={CARD_ELEMENT_OPTIONS}
            className="form-control"
            onChange={handleCardCvcChange}
          />
          {cardErrors.cardCvc && (
            <p className={styles.fieldError}>
              {cardErrors.cardCvc}
            </p>
          )}
        </label>
      </div>

      <CustomInput
        type="text"
        label="Name on Card"
        value={cardholderName}
        onChange={handleCardholderNameChange}
      />
      {cardholderNameError && (
        <p className={styles.fieldError}>
          {cardholderNameError}
        </p>
      )}

      <CountrySelect
        defaultCountryCode={address.country}
        onCountryChange={(countryCode) => {
          dispatch(setPaymentFormAddress({ ...address, country: countryCode }));
          setShowAddressInputs(true);
        }}
      />
      {addressErrors.country && (
        <p className={styles.fieldError}>
          {addressErrors.country}
        </p>
      )}
      {!!stateOptions.length &&
        <DropdownSelect
          label="Select state or province"
          value={stateOptions.find(o => o.value === address.state) ?? stateOptions[0]}
          options={stateOptions}
          onChange={option => {
            if (option) {
              dispatch(setPaymentFormAddress({ ...address, state: option.value }))
            }
          }}
          isSearchable={true}
          blurInputOnSelect
          withDescription
          placeholder="Select country"
        />
      }

      {showAddressInputs && (
        <>
          <CustomInput
            type="text"
            label="Address line 1"
            value={address.line1}
            onChange={(e) =>
              dispatch(setPaymentFormAddress({ ...address, line1: e.target.value }))
            }
          />
          {addressErrors.line1 && (
            <p className={styles.fieldError}>
              {addressErrors.line1}
            </p>
          )}
          <CustomInput
            type="text"
            label="Address line 2"
            value={address.line2}
            onChange={(e) =>
              dispatch(setPaymentFormAddress({ ...address, line2: e.target.value }))
            }
          />
          <CustomInput
            type="text"
            label="Postal code"
            value={address.postalCode}
            onChange={(e) =>
              dispatch(setPaymentFormAddress({ ...address, postalCode: e.target.value }))
            }
          />
          {addressErrors.postalCode && (
            <p className={styles.fieldError}>
              {addressErrors.postalCode}
            </p>
          )}
          <CustomInput
            type="text"
            label="City"
            value={address.city}
            onChange={(e) =>
              dispatch(setPaymentFormAddress({ ...address, city: e.target.value }))
            }
          />
          {addressErrors.city && (
            <p className={styles.fieldError}>
              {addressErrors.city}
            </p>
          )}
        </>
      )}
      {errorMessage && (
        <div className={styles.fieldError}>{errorMessage}</div>
      )}
    </form>
  );
});

export default PaymentForm;
